import { SortTypes } from "./interface.utils";

export const TARGET_IMAGE_DIMENSIONS_CANVAS = 750;

// Upload size limits, in MB (1 MB = 1024 * 1024 bytes, as the drop zone counts it). They mirror
// the server's caps in augmo-backend src/utils/pricing.utils.ts, which enforces them as well.
export const IMAGE_MAX_SIZE_MB = 3;
export const VIDEO_MAX_SIZE_MB = 100;

// Smallest target image the editor accepts, in pixels. Mirrors augmo-backend's
// image-target-compiler/constants.json minimumWidth/minimumHeight -- the same floor the server's
// crop validation enforces during compile.
export const TARGET_IMAGE_MIN_WIDTH = 240;
export const TARGET_IMAGE_MIN_HEIGHT = 320;

// What the video upload card tells the user a target costs: one flat price,
// whatever the video's size (up to VIDEO_MAX_SIZE_MB). DISPLAY ONLY: copied from augmo-backend
// src/utils/pricing.utils.ts and never used to work out a charge -- the server computes every
// amount itself. If the server's price changes, update this by hand (docs/ISSUES.md #30).
export const TARGET_PRICE_PAISE = 12900;

// The project QR code drawn on each downloaded target image: a white square in the bottom-right
// corner, sized and inset as a share of the image's shorter side. Below the minimum pixel size a
// phone cannot read a level-H code, so smaller images are scaled up until the box reaches it.
export const TARGET_QR_SIZE_RATIO = 0.15;
export const TARGET_QR_INSET_RATIO = 0.03;
export const TARGET_QR_MIN_SIZE_PX = 200;
// The brand logo in the middle of every project QR code (download and preview screen): a white
// square 28% of the code's width with the logo inside it. Level H lets a code be read with up to
// about 30% of it covered, which leaves the logo a wide margin.
export const TARGET_QR_ERROR_CORRECTION_LEVEL = 'H';
export const TARGET_QR_LOGO_RATIO = 0.28;
export const TARGET_QR_LOGO_PADDING_RATIO = 0.1;
// Pixel size of src/assets/icons/qr_logo.svg, for drawing it before it has loaded.
export const QR_LOGO_WIDTH = 228;
export const QR_LOGO_HEIGHT = 300;
export const TARGET_QR_BACKING_COLOR = '#ffffff';
// The hidden canvas the QR code is copied from: large enough to stay sharp on a big target
// image, with the 4-module quiet zone the QR standard asks for.
export const TARGET_QR_SOURCE_SIZE_PX = 1024;
export const TARGET_QR_QUIET_ZONE_MODULES = 4;
export const TARGET_IMAGE_JPEG_QUALITY = 0.92;

// The folder (and zip) the downloaded target images go into is named after the project.
export const PROJECT_IMAGES_FOLDER_NAME_MAX_LENGTH = 100;
export const PROJECT_IMAGES_FALLBACK_FOLDER_NAME = 'project';
// Razorpay's hosted checkout, loaded on demand by adapters/razorpay.
export const RAZORPAY_CHECKOUT_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
export const ZIP_MIME_TYPE = 'application/zip';
// The QR code on the preview screen is saved as a PNG named after the project.
export const QR_CODE_MIME_TYPE = 'image/png';
export const QR_CODE_FILE_SUFFIX = '-qr-code.png';

// Account settings > My bills asks the server for this many invoices at a time.
export const INVOICE_PAGE_SIZE = 10;
// Projects per page on the welcome screen; matches the server's default page size.
export const PROJECT_PAGE_SIZE = 10;

export const SORT_TYPES = [
  {
    label: 'Name A to Z',
    value: SortTypes.Data_Ascending,
  },
  {
    label: 'Name Z to A',
    value: SortTypes.Data_Descending,
  },
  {
    label: 'Last modified',
    value: SortTypes.Last_Modified,
  },
];
// Screens only an admin account may open. Matched as an exact path or a "<prefix>/..." child.
export const ADMIN_PATH_PREFIXES = ['/admin'];

// Screens that belong to an ordinary user's own work and billing: an admin is sent to /admin instead.
export const USER_ONLY_PATH_PREFIXES = ['/welcome', '/support', '/upgrade-plan'];

// Sections of the admin sidebar, in display order. `route` is also the prefix used to mark one active.
export const ADMIN_NAV_ITEMS: { key: 'overview' | 'users' | 'projects' | 'tickets'; label: string; route: string }[] = [
  { key: 'overview', label: 'Overview', route: '/admin/overview' },
  { key: 'users', label: 'Users', route: '/admin/users' },
  { key: 'projects', label: 'Projects', route: '/admin/projects' },
  { key: 'tickets', label: 'Tickets', route: '/admin/tickets' },
];


// Status pill tones. The tone names map to the colours defined in status_badge.ui.scss; a status that is not
// listed renders neutral.
export type StatusTone = 'positive' | 'warning' | 'info' | 'neutral';

export const STATUS_TONES: Record<string, StatusTone> = {
  published: 'positive',
  active: 'positive',
  resolved: 'positive',
  draft: 'warning',
  open: 'warning',
  in_progress: 'info',
  archived: 'neutral',
  closed: 'neutral',
  deleted: 'neutral',
};
export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 120;
